import { useEffect, useRef, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";

/* v2.95 · Harmonics spectrum editor.
   Displays each harmonic order as an interactive vertical bar. Users
   drag the top of a bar to change its magnitude, or click "+ harmonic"
   to add a new order. Emits `{ voltage_kv, fundamental_current_a,
   Isc_a, harmonics: [{h, magnitude_pu}] }`. */

const CANVAS_H = 320;
const PAD_T = 24;
const PAD_B = 48;
const MAX_PU = 0.30;                          // top of the axis

export default function HarmonicsSpectrum({ onChange }) {
  const [voltage, setVoltage] = useState(13.8);
  const [I1, setI1] = useState(1000);
  const [Isc, setIsc] = useState(25000);
  const [bars, setBars] = useState([
    { h: 5,  magnitude_pu: 0.038 },
    { h: 7,  magnitude_pu: 0.025 },
    { h: 11, magnitude_pu: 0.015 },
    { h: 13, magnitude_pu: 0.010 },
  ]);
  const [selected, setSelected] = useState(0);
  const svgRef = useRef(null);
  const dragIdx = useRef(null);

  useEffect(() => {
    onChange?.({
      voltage_kv: voltage,
      fundamental_current_a: I1,
      Isc_a: Isc,
      harmonics: bars,
    });
  }, [voltage, I1, Isc, bars, onChange]);

  const startDrag = (i) => (e) => {
    e.preventDefault();
    dragIdx.current = i;
    setSelected(i);
  };
  const onMove = (e) => {
    if (dragIdx.current === null) return;
    const rect = svgRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const usable = CANVAS_H - PAD_T - PAD_B;
    let pu = ((CANVAS_H - PAD_B - y) / usable) * MAX_PU;
    pu = Math.max(0, Math.min(MAX_PU, pu));
    setBars((prev) => prev.map((b, ix) =>
      ix === dragIdx.current ? { ...b, magnitude_pu: Number(pu.toFixed(3)) } : b));
  };
  const endDrag = () => { dragIdx.current = null; };

  const addBar = () => {
    const takenOrders = new Set(bars.map((b) => b.h));
    let next = 3;
    while (takenOrders.has(next)) next += 2;
    setBars([...bars, { h: next, magnitude_pu: 0.005 }]);
    setSelected(bars.length);
  };
  const removeBar = (i) => {
    const next = bars.filter((_, ix) => ix !== i);
    setBars(next); setSelected(Math.min(selected, next.length - 1));
  };
  const patchBar = (i, patch) => {
    setBars((prev) => prev.map((b, ix) => ix === i ? { ...b, ...patch } : b));
  };

  const maxOrder = Math.max(25, ...bars.map((b) => b.h));
  const xFor = (h) => 48 + (h - 1) * (620 / (maxOrder + 1));
  const yFor = (pu) => CANVAS_H - PAD_B - (pu / MAX_PU) * (CANVAS_H - PAD_T - PAD_B);

  return (
    <div className="border border-white/10 bg-[#0A0C10] relative" data-testid="hm-spectrum">
      {/* Top mini-toolbar for PCC parameters. */}
      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-white/10">
        <label className="text-[10px] font-mono text-gray-400 flex items-center gap-1">
          voltage <input type="number" step="0.1" value={voltage}
            onChange={(e) => setVoltage(Number(e.target.value))}
            className="w-20 bg-black/40 border border-white/10 px-1 py-0.5 text-[11px]"
            data-testid="hm-canvas-kv" /> kV
        </label>
        <label className="text-[10px] font-mono text-gray-400 flex items-center gap-1">
          I₁ <input type="number" value={I1}
            onChange={(e) => setI1(Number(e.target.value))}
            className="w-24 bg-black/40 border border-white/10 px-1 py-0.5 text-[11px]"
            data-testid="hm-canvas-i1" /> A
        </label>
        <label className="text-[10px] font-mono text-gray-400 flex items-center gap-1">
          Isc <input type="number" value={Isc}
            onChange={(e) => setIsc(Number(e.target.value))}
            className="w-28 bg-black/40 border border-white/10 px-1 py-0.5 text-[11px]"
            data-testid="hm-canvas-isc" /> A
        </label>
        <button onClick={addBar}
          className="ml-auto text-[10px] font-mono text-[#00E5FF] hover:bg-white/5 px-2 py-1 border border-white/10"
          data-testid="hm-add-bar">
          <Plus size={10} className="inline" /> harmonic
        </button>
      </div>

      <svg ref={svgRef}
        viewBox={`0 0 720 ${CANVAS_H}`} className="w-full block cursor-crosshair"
        onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}>
        {/* axis lines + grid */}
        <line x1={40} x2={680} y1={CANVAS_H - PAD_B} y2={CANVAS_H - PAD_B}
              stroke="#ffffff20" strokeWidth="0.5" />
        {[0, 0.05, 0.10, 0.15, 0.20, 0.25].map((pu) => (
          <g key={pu}>
            <line x1={40} x2={680} y1={yFor(pu)} y2={yFor(pu)} stroke="#ffffff08" />
            <text x={34} y={yFor(pu) + 3} textAnchor="end"
                  fill="#8792A2" fontFamily="monospace" fontSize="9">{`${(pu * 100).toFixed(0)}%`}</text>
          </g>
        ))}
        {/* order ticks */}
        {[1, 5, 7, 11, 13, 17, 19, 23, 25].filter((h) => h <= maxOrder + 1).map((h) => (
          <g key={h}>
            <line x1={xFor(h)} x2={xFor(h)} y1={CANVAS_H - PAD_B} y2={CANVAS_H - PAD_B + 4}
                  stroke="#8792A2" strokeWidth="0.5" />
            <text x={xFor(h)} y={CANVAS_H - PAD_B + 14} textAnchor="middle"
                  fill="#8792A2" fontFamily="monospace" fontSize="9">{h}</text>
          </g>
        ))}
        <text x={360} y={CANVAS_H - 6} textAnchor="middle"
              fill="#8792A2" fontFamily="monospace" fontSize="10">harmonic order (h)</text>

        {/* fundamental reference */}
        <line x1={xFor(1)} x2={xFor(1)} y1={yFor(0)} y2={yFor(0.28)}
              stroke="#FFCC00" strokeWidth="3" strokeDasharray="2,3" />
        <text x={xFor(1)} y={yFor(0.28) - 4} textAnchor="middle"
              fill="#FFCC00" fontFamily="monospace" fontSize="9">1.00 pu</text>

        {/* Harmonic bars */}
        {bars.map((b, i) => {
          const x = xFor(b.h);
          const y = yFor(b.magnitude_pu);
          const active = selected === i;
          return (
            <g key={i}
               onMouseDown={startDrag(i)}
               onClick={() => setSelected(i)}
               className="cursor-ns-resize"
               data-testid={`hm-bar-${i}`}>
              <rect x={x - 8} y={y}
                    width="16" height={CANVAS_H - PAD_B - y}
                    fill={active ? "#00E5FF" : "#00E5FFAA"}
                    stroke={active ? "#FFFFFF" : "#00E5FF"} strokeWidth={active ? 2 : 1} />
              <text x={x} y={y - 4} textAnchor="middle" fill="#FFFFFF"
                    fontFamily="monospace" fontSize="9">
                {(b.magnitude_pu * 100).toFixed(1)}%
              </text>
            </g>
          );
        })}
      </svg>

      {/* Selected bar edit inputs */}
      {bars[selected] && (
        <div className="flex flex-wrap items-center gap-2 p-2 border-t border-white/10 bg-[#12151C]"
             data-testid="hm-bar-editor">
          <span className="text-[10px] font-mono text-gray-500">selected h =</span>
          <input type="number" min={2} value={bars[selected].h}
            onChange={(e) => patchBar(selected, { h: Number(e.target.value) })}
            className="w-14 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono"
            data-testid="hm-bar-order" />
          <span className="text-[10px] font-mono text-gray-500">magnitude (pu)</span>
          <input type="number" step="0.001" min={0} max={MAX_PU} value={bars[selected].magnitude_pu}
            onChange={(e) => patchBar(selected, { magnitude_pu: Number(e.target.value) })}
            className="w-24 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono"
            data-testid="hm-bar-pu" />
          <button onClick={() => removeBar(selected)}
            className="ml-auto text-[10px] text-[#FF3B30] hover:underline inline-flex items-center gap-1"
            data-testid="hm-bar-delete">
            <Trash size={10} /> remove
          </button>
        </div>
      )}
    </div>
  );
}
